/**
 * CanvasHtmlStreamPreviewContext — live HTML painting into loading ghosts.
 *
 * While the agent streams a `Tool_Toolbox_FileRecords_Create` call for an
 * HTML file, this provider accumulates the tool-call args off the existing
 * SSE stream (`flow_message.tool_call_args_delta`), permissively parses the
 * partial JSON (strings included — the trailing unterminated `content` field
 * IS the payload), and exposes the growing HTML keyed by the call's
 * `reference_id`, which is exactly the ghost node's Liveblocks id.
 *
 * EntityNode's skeleton branch subscribes per node id and renders the
 * partial document in a sandboxed iframe, so the user watches the block
 * paint itself while the agent writes it. When the real node lands (backend
 * ReplaceNodeDataOp drops `loading`), the preview is discarded.
 *
 * One provider-level SSE handler serves the whole canvas; per-node
 * subscriptions go through useSyncExternalStore so a delta only re-renders
 * the ghost it belongs to.
 */
import { useEventStream } from "@/providers/EventStreamProvider";
import type { CanvasBlockPresentation } from "@cohesive/ui-components";
import { Allow, parse } from "partial-json";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";

const FILE_RECORD_CREATE_TOOL_NAME = "Tool_Toolbox_FileRecords_Create";
/** Trailing-edge throttle between preview emits per ghost. */
const EMIT_INTERVAL_MS = 500;
/** Minimum HTML growth between emits — avoids reloading the preview iframe
 *  for a handful of streamed tokens. */
const EMIT_MIN_GROWTH_BYTES = 256;
/** Hard cap on accumulated args per call; beyond this the preview downgrades
 *  to the plain skeleton (the real card still lands normally). */
const MAX_ACCUMULATED_ARGS_BYTES = 1_000_000;
/** Entries evict this long after their last delta — covers abandoned calls
 *  (stop, error) whose ghosts the backend already cleans up. */
const ENTRY_TTL_MS = 10 * 60 * 1000;

export interface HtmlStreamPreview {
  /** Partial (or complete) HTML document streamed so far. */
  html: string;
  /** Presentation parsed from the same args, when already streamed. */
  presentation?: CanvasBlockPresentation;
}

interface StreamEntry {
  accumulatedArgs: string;
  overflowed: boolean;
  preview: HtmlStreamPreview | null;
  lastEmitLength: number;
  emitTimer: ReturnType<typeof setTimeout> | null;
  evictTimer: ReturnType<typeof setTimeout> | null;
}

interface StreamPreviewStore {
  getPreview(nodeId: string): HtmlStreamPreview | null;
  subscribe(nodeId: string, onChange: () => void): () => void;
}

const CanvasHtmlStreamPreviewContext = createContext<StreamPreviewStore | null>(
  null,
);

/** Parse partial tool-call args permissively (partial strings allowed) and
 *  project out the block preview. Returns null until the args look like an
 *  HTML content-mode create with some content streamed. */
function projectPreview(accumulatedArgs: string): HtmlStreamPreview | null {
  let parsed: unknown;
  try {
    parsed = parse(accumulatedArgs, Allow.OBJ | Allow.ARR | Allow.STR);
  } catch {
    return null;
  }
  if (parsed == null || typeof parsed !== "object") return null;
  const args = parsed as {
    content_type?: unknown;
    content?: unknown;
    canvas_presentation?: unknown;
  };
  // content_type streams before content (schema orders content last), so by
  // the time content bytes exist this check is reliable; a half-streamed
  // "text/ht" simply defers the first paint one tick.
  if (args.content_type !== "text/html") return null;
  if (typeof args.content !== "string" || args.content.length === 0) {
    return null;
  }
  const presentation =
    args.canvas_presentation != null &&
    typeof args.canvas_presentation === "object"
      ? (args.canvas_presentation as CanvasBlockPresentation)
      : undefined;
  return { html: args.content, presentation };
}

export function CanvasHtmlStreamPreviewProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { registerHandler } = useEventStream();
  const entriesRef = useRef<Map<string, StreamEntry>>(new Map());
  const listenersRef = useRef<Map<string, Set<() => void>>>(new Map());

  const notify = useCallback((nodeId: string) => {
    listenersRef.current.get(nodeId)?.forEach((listener) => listener());
  }, []);

  useEffect(() => {
    const entries = entriesRef.current;

    const dropEntry = (nodeId: string) => {
      const entry = entries.get(nodeId);
      if (!entry) return;
      if (entry.emitTimer) clearTimeout(entry.emitTimer);
      if (entry.evictTimer) clearTimeout(entry.evictTimer);
      entries.delete(nodeId);
      notify(nodeId);
    };

    const unregister = registerHandler((event) => {
      if (event.type !== "flow_message.tool_call_args_delta") return;
      const payload = event.data.flow_message_tool_call_args_delta;
      const call = payload.tool_call;
      if (call?.function?.name !== FILE_RECORD_CREATE_TOOL_NAME) return;
      const nodeId = call.reference_id;
      if (!nodeId) return;

      let entry = entries.get(nodeId);
      if (!entry) {
        entry = {
          accumulatedArgs: "",
          overflowed: false,
          preview: null,
          lastEmitLength: 0,
          emitTimer: null,
          evictTimer: null,
        };
        entries.set(nodeId, entry);
      }
      if (entry.overflowed) return;

      entry.accumulatedArgs += payload.args_delta ?? "";
      if (entry.accumulatedArgs.length > MAX_ACCUMULATED_ARGS_BYTES) {
        // Too large to keep re-parsing — fall back to the plain skeleton.
        entry.overflowed = true;
        entry.accumulatedArgs = "";
        entry.preview = null;
        notify(nodeId);
        return;
      }

      if (entry.evictTimer) clearTimeout(entry.evictTimer);
      entry.evictTimer = setTimeout(() => dropEntry(nodeId), ENTRY_TTL_MS);

      if (entry.emitTimer) return; // trailing-edge throttle already armed
      entry.emitTimer = setTimeout(() => {
        entry.emitTimer = null;
        const preview = projectPreview(entry.accumulatedArgs);
        if (!preview) return;
        const grewEnough =
          preview.html.length - entry.lastEmitLength >= EMIT_MIN_GROWTH_BYTES;
        const presentationArrived =
          preview.presentation != null && entry.preview?.presentation == null;
        if (entry.preview != null && !grewEnough && !presentationArrived) {
          return;
        }
        entry.lastEmitLength = preview.html.length;
        entry.preview = preview;
        notify(nodeId);
      }, EMIT_INTERVAL_MS);
    });

    return () => {
      unregister();
      for (const entry of entries.values()) {
        if (entry.emitTimer) clearTimeout(entry.emitTimer);
        if (entry.evictTimer) clearTimeout(entry.evictTimer);
      }
      entries.clear();
    };
  }, [registerHandler, notify]);

  const store = useMemo<StreamPreviewStore>(
    () => ({
      getPreview: (nodeId) => entriesRef.current.get(nodeId)?.preview ?? null,
      subscribe: (nodeId, onChange) => {
        let set = listenersRef.current.get(nodeId);
        if (!set) {
          set = new Set();
          listenersRef.current.set(nodeId, set);
        }
        set.add(onChange);
        return () => {
          set.delete(onChange);
          if (set.size === 0) listenersRef.current.delete(nodeId);
        };
      },
    }),
    [],
  );

  return (
    <CanvasHtmlStreamPreviewContext.Provider value={store}>
      {children}
    </CanvasHtmlStreamPreviewContext.Provider>
  );
}

/**
 * The streaming HTML preview for one ghost node, or null when nothing is
 * streaming for it (or no provider is mounted — capture/public surfaces).
 * Re-renders only on this node's own emits.
 */
export function useHtmlBlockStreamPreview(
  nodeId: string,
): HtmlStreamPreview | null {
  const store = useContext(CanvasHtmlStreamPreviewContext);
  const subscribe = useCallback(
    (onChange: () => void) =>
      store ? store.subscribe(nodeId, onChange) : () => {},
    [store, nodeId],
  );
  const getSnapshot = useCallback(
    () => (store ? store.getPreview(nodeId) : null),
    [store, nodeId],
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}
